<script lang="ts">
  import Mail from '@lucide/svelte/icons/mail';
  import Clock from '@lucide/svelte/icons/clock';
  import ShieldCheck from '@lucide/svelte/icons/shield-check';
  import LampGlow from '$lib/components/landing/effects/lamp-glow.svelte';
  import ContactForm from '$lib/components/landing/contact-form.svelte';
  import { CONTACT_ANCHOR, SUPPORT_EMAIL } from '$lib/utils/site';

  let {
    title = 'Good teaching',
    emphasis = 'starts with you.',
    lead = 'Tell us about your institution and what you need. Let us help you find the right place to start.',
    headingLevel = 'h2'
  }: { title?: string; emphasis?: string; lead?: string; headingLevel?: 'h1' | 'h2' } = $props();
</script>

<section id={CONTACT_ANCHOR} class="closing" aria-labelledby="contact-heading">
  <div class="closing-panel">
    <LampGlow />
    <div class="closing-grid">
      <div class="closing-content">
        <svelte:element this={headingLevel} id="contact-heading" data-reveal class:hero-heading={headingLevel === 'h1'}
          >{title}<br /><em>{emphasis}</em></svelte:element
        >
        <p>{lead}</p>
        <ul class="closing-points">
          <li><Clock size={15} />Share your subjects and student count for a tailored reply.</li>
          <li><ShieldCheck size={15} />We use your details only to respond to your enquiry.</li>
        </ul>
        <a class="closing-email" href="mailto:{SUPPORT_EMAIL}"><Mail size={17} />{SUPPORT_EMAIL}</a>
      </div>
      <ContactForm />
    </div>
  </div>
</section>

<style>
  .closing {
    width: min(1120px, calc(100% - 80px));
    margin-inline: auto;
    padding-block: 10px 110px;
    scroll-margin-top: 90px;
  }
  .closing-panel {
    position: relative;
    isolation: isolate;
    overflow: hidden;
    padding: 100px 48px 56px;
    border-radius: 28px;
    background:
      radial-gradient(ellipse 80% 60% at 50% 120%, #1447e622, transparent 70%),
      linear-gradient(180deg, #161714, #22231f);
    color: #ffffed;
    box-shadow: 0 40px 80px -50px #11132a88;
  }
  .closing-grid {
    position: relative;
    z-index: 1;
    display: grid;
    grid-template-columns: 0.9fr 1.15fr;
    gap: 45px;
    align-items: center;
  }
  .closing-content {
    text-align: left;
  }
  .closing-content > :global(:is(h1, h2)) {
    margin: 0;
    font-family: 'Instrument Serif', Georgia, serif;
    font-weight: 400;
    font-size: clamp(48px, 5vw, 67px);
    line-height: 1.04;
    letter-spacing: -0.035em;
    text-wrap: balance;
  }
  .closing-content > :global(.hero-heading) {
    font-size: clamp(54px, 6vw, 80px);
  }
  .closing-content em {
    font-weight: 400;
    padding-right: 0.06em;
    color: #c9d6ff;
  }
  .closing-content > p {
    max-width: 360px;
    margin: 26px 0 22px;
    color: #d3d7e1;
    font-size: 14px;
    line-height: 1.8;
  }
  .closing-points {
    display: grid;
    gap: 12px;
    margin: 0 0 28px;
    padding: 0;
    list-style: none;
  }
  .closing-points li {
    display: flex;
    align-items: center;
    gap: 10px;
    color: #b9c2d4;
    font-size: 12px;
  }
  .closing-points :global(svg) {
    flex-shrink: 0;
    color: #f4edac;
  }
  .closing-email {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    font-size: 13px;
    color: #d6e0ff;
    text-decoration: underline;
    text-underline-offset: 5px;
    transition: color 200ms;
  }
  .closing-email:hover {
    color: #f4edac;
  }
  .closing-panel :global(:focus-visible) {
    outline-color: #d9e2ff;
  }
  @media (max-width: 760px) {
    .closing {
      width: calc(100% - 24px);
      padding-block: 0 48px;
    }
    .closing-panel {
      padding: 86px 16px 16px;
      border-radius: 22px;
    }
    .closing-grid {
      grid-template-columns: 1fr;
      gap: 30px;
    }
    .closing-content {
      text-align: center;
    }
    .closing-content > :global(:is(h1, h2)) {
      font-size: clamp(42px, 12vw, 52px);
    }
    .closing-content > p {
      margin-inline: auto;
      padding-inline: 6px;
    }
    .closing-points {
      width: fit-content;
      max-width: 320px;
      margin: 0 auto 22px;
      text-align: left;
    }
  }
</style>
